import { View } from "@-/com.npmjs.react-native";
import {
  LearnMapCardsBackdrop,
  useIsLearnMapOpen,
  useLearnCardsPathname,
  useLearnMapModeSelector,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import type { ReactNode } from "react";

/** The routes whose screen is the persistent deck or summary, drawn by this layer (not the Stack). */
function isLearnCardsRoute(pathname: string): boolean {
  return (
    pathname.endsWith("/learn") ||
    pathname.endsWith("/learn/summary") ||
    pathname.endsWith("/learn/deck") ||
    pathname.includes("/learn/lesson/slide/")
  );
}

/**
 * Learn's persistent cards (the deck, the summary over it, and the recharge bar) on the cards' own
 * backdrop. The layer itself never moves. When Learn's map opens, only the card slides up and out
 * (`useLearnMapCardSlideStyle`), over the recharge bar and Learn's toolbar; the backdrop dissolves
 * into the map beneath while the card covers the toolbar, and the controls below the card stay
 * where they are until the map's sheet slides in over them. Closing the map reverses all of it.
 *
 * On the cards' routes the layer draws above Learn's Stack, whose screens there are transparent
 * history entries, so the deck and the summary take the touches. On every other Learn route the
 * Stack's own page draws over it.
 */
export function LearnCardsLayer({ children }: { children: ReactNode }) {
  return (
    <LearnCardsRequested>
      <LearnCardsLayerContent>{children}</LearnCardsLayerContent>
    </LearnCardsRequested>
  );
}

/** Mounts card UI only after an explicit request; a map launch renders none of its children. */
export function LearnCardsRequested({ children }: { children: ReactNode }) {
  const areCardsRequested = useLearnMapModeSelector((mode) => mode.areCardsRequested) ?? true;
  return areCardsRequested ? children : null;
}

function LearnCardsLayerContent({ children }: { children: ReactNode }) {
  const pathname = useLearnCardsPathname();
  const isMapOpen = useIsLearnMapOpen();
  // From the press that opens the map: the controls left on screen must not take a tap meant for
  // the map's sheet sliding over them.
  const isLeavingForMap = useLearnMapModeSelector((mode) => mode.isMapToolbarCurrent) ?? false;
  const isAway = isMapOpen || isLeavingForMap;
  // A settled map never paints cards, even before their animated styles or clipping measurements
  // reach the native views. After the first request, retain the session for later map visits;
  // a press to leave restores the layer before the return slide starts.
  const isMapSettled = isMapOpen && isLeavingForMap;
  return (
    <View
      accessibilityElementsHidden={isAway}
      className={`${isLearnCardsRoute(pathname) ? "absolute inset-0 z-10" : "absolute inset-0"}${isMapSettled ? " opacity-0" : ""}`}
      importantForAccessibility={isAway ? "no-hide-descendants" : "auto"}
      pointerEvents={isAway ? "none" : "box-none"}
      testID="learn-cards-layer"
    >
      <LearnMapCardsBackdrop />
      {children}
    </View>
  );
}
